import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  IconButton,
  Button,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useColorModeValue,
  Tooltip,
  Badge,
} from '@chakra-ui/react';
import {
  AddIcon,
  DeleteIcon,
  EditIcon,
  CheckIcon,
  CloseIcon,
} from '@chakra-ui/icons';
import { Session, sessionService } from '../../services/sessionService';

interface SessionManagerProps {
  currentSessionId: string | null;
  onSessionChange: (session: Session) => void;
  onNewSession: () => void;
}

const SessionManager: React.FC<SessionManagerProps> = ({
  currentSessionId,
  onSessionChange,
  onNewSession,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [sessions, setSessions] = useState<Session[]>(() =>
    sessionService.loadSessions()
  );
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const activeBg = useColorModeValue('primary.50', 'primary.900');

  const refreshSessions = () => {
    setSessions(sessionService.loadSessions());
  };

  // Refresh sessions list when modal opens
  useEffect(() => {
    if (isOpen) {
      refreshSessions();
    }
  }, [isOpen]);

  const handleStartEdit = (session: Session) => {
    setEditingId(session.id);
    setEditName(session.name);
  };

  const handleSaveEdit = (sessionId: string) => {
    if (editName.trim()) {
      sessionService.renameSession(sessionId, editName.trim());
      refreshSessions();
    }
    setEditingId(null);
    setEditName('');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const handleDelete = (sessionId: string) => {
    sessionService.deleteSession(sessionId);
    refreshSessions();
  };

  const handleSelectSession = (session: Session) => {
    onSessionChange(session);
    onClose();
  };

  const handleNewSession = () => {
    onNewSession();
    refreshSessions();
    onClose();
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <>
      <Tooltip label='Manage Sessions' placement='left'>
        <IconButton
          aria-label='Manage sessions'
          icon={<AddIcon />}
          size='sm'
          variant='ghost'
          color='white'
          onClick={onOpen}
          _hover={{ bg: 'whiteAlpha.200' }}
        />
      </Tooltip>

      <Modal isOpen={isOpen} onClose={onClose} size='md'>
        <ModalOverlay />
        <ModalContent bg={bgColor}>
          <ModalHeader>Chat Sessions</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <VStack spacing={2} align='stretch' maxH='400px' overflowY='auto'>
              {sessions.length === 0 ? (
                <Text color='gray.500' textAlign='center' py={8}>
                  No saved sessions yet
                </Text>
              ) : (
                sessions
                  .sort((a, b) => b.updatedAt - a.updatedAt)
                  .map(session => (
                    <Box
                      key={session.id}
                      p={3}
                      borderWidth='1px'
                      borderColor={borderColor}
                      borderRadius='md'
                      bg={
                        session.id === currentSessionId
                          ? activeBg
                          : 'transparent'
                      }
                      _hover={{ bg: hoverBg }}
                      cursor='pointer'
                      onClick={() => handleSelectSession(session)}
                    >
                      <HStack justify='space-between' align='start'>
                        <VStack align='start' spacing={1} flex={1}>
                          {editingId === session.id ? (
                            <HStack width='100%'>
                              <Input
                                size='sm'
                                value={editName}
                                onChange={e => setEditName(e.target.value)}
                                onKeyPress={e => {
                                  if (e.key === 'Enter')
                                    handleSaveEdit(session.id);
                                }}
                                autoFocus
                                onClick={e => e.stopPropagation()}
                              />
                              <IconButton
                                aria-label='Save'
                                icon={<CheckIcon />}
                                size='xs'
                                colorScheme='green'
                                onClick={e => {
                                  e.stopPropagation();
                                  handleSaveEdit(session.id);
                                }}
                              />
                              <IconButton
                                aria-label='Cancel'
                                icon={<CloseIcon />}
                                size='xs'
                                onClick={e => {
                                  e.stopPropagation();
                                  handleCancelEdit();
                                }}
                              />
                            </HStack>
                          ) : (
                            <>
                              <HStack>
                                <Text fontWeight='medium' fontSize='sm'>
                                  {session.name}
                                </Text>
                                {session.id === currentSessionId && (
                                  <Badge colorScheme='primary' fontSize='xs'>
                                    Active
                                  </Badge>
                                )}
                              </HStack>
                              <HStack
                                spacing={3}
                                fontSize='xs'
                                color='gray.500'
                              >
                                <Text>{session.messages.length} messages</Text>
                                <Text>•</Text>
                                <Text>{formatDate(session.updatedAt)}</Text>
                              </HStack>
                            </>
                          )}
                        </VStack>

                        {editingId !== session.id && (
                          <HStack spacing={1}>
                            <IconButton
                              aria-label='Rename session'
                              icon={<EditIcon />}
                              size='xs'
                              variant='ghost'
                              onClick={e => {
                                e.stopPropagation();
                                handleStartEdit(session);
                              }}
                            />
                            <IconButton
                              aria-label='Delete session'
                              icon={<DeleteIcon />}
                              size='xs'
                              variant='ghost'
                              colorScheme='red'
                              onClick={e => {
                                e.stopPropagation();
                                handleDelete(session.id);
                              }}
                            />
                          </HStack>
                        )}
                      </HStack>
                    </Box>
                  ))
              )}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button
              leftIcon={<AddIcon />}
              colorScheme='primary'
              onClick={handleNewSession}
              size='sm'
            >
              New Session
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default SessionManager;
